import React from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Paprika } from '../../../styles/theme';
import type { SideFoodChip } from './useSideSuggestions';

interface Props {
  foods: SideFoodChip[];
  picked: string[];
  onToggle: (id: string) => void;
  /** Shown under the chips when no side uses every picked food together. */
  note?: string;
  /** Horizontal inset, so the chips line up with the rows of the sheet or drawer. */
  inset: number;
}

/**
 * « Avec ce que vous avez » (#853): the fresh foods on the shelf, the most
 * plentiful first, above the accompaniment list. Tapping one or two turns the
 * list into the sides that use them; tapping again lets go. Drawn by the
 * phone's sheet and the desktop drawer alike.
 */
export function SideFoodChips({ foods, picked, onToggle, note, inset }: Props) {
  const { t } = useTranslation();
  if (foods.length === 0) return null;
  return (
    <View style={styles.wrap} testID="side-food-chips">
      <Text style={[styles.label, { paddingHorizontal: inset }]}>{t('sideFoods.label')}</Text>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={[styles.row, { paddingHorizontal: inset }]}
      >
        {foods.map((food) => {
          const on = picked.includes(food.id);
          return (
            <Pressable
              key={food.id}
              onPress={() => onToggle(food.id)}
              style={[styles.chip, on && styles.chipOn]}
              accessibilityRole="button"
              accessibilityState={{ selected: on }}
              accessibilityLabel={food.label}
              testID={`side-food-${food.id}`}
            >
              <Text style={[styles.chipText, on && styles.chipTextOn]}>{food.label}</Text>
              {food.rows > 1 && (
                <Text style={[styles.chipCount, on && styles.chipCountOn]}>{t('sideFoods.rows', { count: food.rows })}</Text>
              )}
            </Pressable>
          );
        })}
      </ScrollView>
      {!!note && (
        <Text style={[styles.note, { marginHorizontal: inset }]} testID="side-foods-note">{note}</Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    gap: 8,
    marginBottom: 12,
  },
  label: {
    fontFamily: 'Inter-Medium',
    fontSize: 12,
    color: Paprika.textSecondary,
  },
  row: {
    gap: 8,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 3,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 100,
    backgroundColor: Paprika.surfaceHighest,
  },
  chipOn: {
    backgroundColor: Paprika.primary,
  },
  chipText: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    color: Paprika.textSecondary,
  },
  chipTextOn: {
    color: Paprika.onPrimary,
  },
  chipCount: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    color: Paprika.textMuted,
  },
  chipCountOn: {
    color: 'rgba(255, 255, 255, 0.75)',
  },
  note: {
    fontFamily: 'Inter-Regular',
    fontSize: 12.5,
    lineHeight: 18,
    color: Paprika.textSecondary,
    backgroundColor: Paprika.surfaceLow,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
});
